import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { QuoteProvider } from "@/contexts/QuoteContext";
import { UTMProvider } from "@/contexts/UTMContext";


// Deferred loading wrappers - load after first paint / idle time
import { DeferredAnalytics } from "@/components/DeferredAnalytics";
import { DeferredUI } from "@/components/DeferredUI";

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Services = lazy(() => import("./pages/Services"));
const ServiceArea = lazy(() => import("./pages/ServiceArea"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const OurLicence = lazy(() => import("./pages/about/OurLicence"));
const Reviews = lazy(() => import("./pages/Reviews"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Resources = lazy(() => import("./pages/Resources"));
const EventPlanningChecklistArticle = lazy(() => import("./pages/resources/EventPlanningChecklistArticle"));
const SafetyGuidelinesArticle = lazy(() => import("./pages/resources/SafetyGuidelinesArticle"));
const SpaceSetupArticle = lazy(() => import("./pages/resources/SpaceSetupArticle"));
const GroupSizeArticle = lazy(() => import("./pages/resources/GroupSizeArticle"));
const VenueRequirementsArticle = lazy(() => import("./pages/resources/VenueRequirementsArticle"));
const WhatToExpectArticle = lazy(() => import("./pages/resources/WhatToExpectArticle"));
const ConcessionLineFlowArticle = lazy(() => import("./pages/resources/ConcessionLineFlowArticle"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CancellationPolicy = lazy(() => import("./pages/CancellationPolicy"));
const Waiver = lazy(() => import("./pages/Waiver"));
const PaymentOptions = lazy(() => import("./pages/PaymentOptions"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const BounceHousesCombos = lazy(() => import("./pages/services/BounceHousesCombos"));
const ToddlerInflatables = lazy(() => import("./pages/services/ToddlerInflatables"));
const ObstacleCourses = lazy(() => import("./pages/services/ObstacleCourses"));
const Slides = lazy(() => import("./pages/services/Slides"));
const InteractiveInflatables = lazy(() => import("./pages/services/InteractiveInflatables"));
const ElectronicGames = lazy(() => import("./pages/services/ElectronicGames"));
const DunkTanks = lazy(() => import("./pages/services/DunkTanks"));
const PartyGamesConcessions = lazy(() => import("./pages/services/PartyGamesConcessions"));
const TableChairRentals = lazy(() => import("./pages/services/TableChairRentals"));
const TSSAInflatables = lazy(() => import("./pages/services/TSSAInflatables"));
const LawnSigns = lazy(() => import("./pages/services/LawnSigns"));
const InflatableSports = lazy(() => import("./pages/services/InflatableSports"));
const Packages = lazy(() => import("./pages/Packages"));
const PackageBuilder = lazy(() => import("./pages/PackageBuilder"));
const Windsor = lazy(() => import("./pages/service-area/Windsor"));
const Essex = lazy(() => import("./pages/service-area/Essex"));
const SarniaLambton = lazy(() => import("./pages/service-area/SarniaLambton"));
const London = lazy(() => import("./pages/service-area/London"));
const ChathamKent = lazy(() => import("./pages/service-area/ChathamKent"));
const LaSalle = lazy(() => import("./pages/service-area/LaSalle"));
const Tecumseh = lazy(() => import("./pages/service-area/Tecumseh"));
const Amherstburg = lazy(() => import("./pages/service-area/Amherstburg"));
const BounceHouseWindsor = lazy(() => import("./pages/service-area/BounceHouseWindsor"));
const PartyRentalsWindsor = lazy(() => import("./pages/service-area/PartyRentalsWindsor"));
const Events = lazy(() => import("./pages/Events"));
const Corporate = lazy(() => import("./pages/events/Corporate"));
const Schools = lazy(() => import("./pages/events/Schools"));
const Churches = lazy(() => import("./pages/events/Churches"));
const CommunityMunicipal = lazy(() => import("./pages/events/CommunityMunicipal"));
const BackyardParties = lazy(() => import("./pages/events/BackyardParties"));
const LastMinuteDeals = lazy(() => import("./pages/LastMinuteDeals"));
const AddOns = lazy(() => import("./pages/AddOns"));
const Categories = lazy(() => import("./pages/Categories"));
const Rentals = lazy(() => import("./pages/Rentals"));
const RentalDetail = lazy(() => import("./pages/RentalDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const InventoryAdmin = lazy(() => import("./pages/admin/Inventory"));
const ContactsAdmin = lazy(() => import("./pages/admin/Contacts"));
const ContactsUpload = lazy(() => import("./pages/admin/ContactsUpload"));
const BookingsUpload = lazy(() => import("./pages/admin/BookingsUpload"));
const QuotesAdmin = lazy(() => import("./pages/admin/Quotes"));
const UsersAdmin = lazy(() => import("./pages/admin/Users"));
const AdminCalendar = lazy(() => import("./pages/admin/Calendar"));
const AdminRoutes = lazy(() => import("./pages/admin/Routes"));
const SettingsAdmin = lazy(() => import("./pages/admin/Settings"));
const UTMLinksAdmin = lazy(() => import("./pages/admin/UTMLinks"));
const AnalyticsAdmin = lazy(() => import("./pages/admin/Analytics"));
const AccountingAdmin = lazy(() => import("./pages/admin/Accounting"));
const SalesAdmin = lazy(() => import("./pages/admin/Sales"));
const EmployeesAdmin = lazy(() => import("./pages/admin/Employees"));
const TimeLogsAdmin = lazy(() => import("./pages/admin/TimeLogs"));

const WebsiteHealth = lazy(() => import("./pages/admin/WebsiteHealth"));
const WebsiteHealthTasks = lazy(() => import("./pages/admin/WebsiteHealthTasks"));
const WebsiteHealthRuns = lazy(() => import("./pages/admin/WebsiteHealthRuns"));
const PagesAdmin = lazy(() => import("./pages/admin/Pages"));
const PromotionsAdmin = lazy(() => import("./pages/admin/Promotions"));
const EmailLogAdmin = lazy(() => import("./pages/admin/EmailLog"));
const EmailTemplatesAdmin = lazy(() => import("./pages/admin/EmailTemplates"));
const CartRecoveryAdmin = lazy(() => import("./pages/admin/CartRecovery"));
const CronJobsAdmin = lazy(() => import("./pages/admin/CronJobs"));
const EmployeeCheckin = lazy(() => import("./pages/EmployeeCheckin"));
const CategoriesAdmin = lazy(() => import("./pages/admin/Categories"));
const InventoryUpload = lazy(() => import("./pages/admin/InventoryUpload"));
const PipelineAdmin = lazy(() => import("./pages/admin/Pipeline"));

// Driver Portal - lazy loaded
const PhotoBooth = lazy(() => import("./pages/PhotoBooth"));
const DriverPortal = lazy(() => import("./pages/driver/DriverPortal"));
const DriverLogin = lazy(() => import("./pages/driver/DriverLogin"));

// AdminRoute eager-loaded to eliminate sequential chunk waterfall on /admin/*
import { AdminRoute } from "./components/admin/AdminRoute";

// Lazy load driver route guards - only needed for specific routes
const DriverRoute = lazy(() => import("./components/driver/DriverRoute").then(m => ({ default: m.DriverRoute })));
const DriverLayout = lazy(() => import("./components/driver/DriverLayout").then(m => ({ default: m.DriverLayout })));

const queryClient = new QueryClient();

// Minimal loading fallback - just a spinner or empty div to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <QuoteProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              {/* Deferred analytics - must be inside BrowserRouter for useLocation */}
              <DeferredAnalytics />
              <UTMProvider>
                {/* Deferred UI - loads FloatingQuoteButton + ScrollToTop after 100ms */}
                <DeferredUI />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Index />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/services/bounce-houses-combos" element={<BounceHousesCombos />} />
                    <Route path="/services/toddler-inflatables" element={<ToddlerInflatables />} />
                    <Route path="/services/obstacle-courses" element={<ObstacleCourses />} />
                    <Route path="/services/slides" element={<Slides />} />
                    <Route path="/services/interactive-inflatables" element={<InteractiveInflatables />} />
                    <Route path="/services/dunk-tanks" element={<DunkTanks />} />
                    <Route path="/services/party-games-concessions" element={<PartyGamesConcessions />} />
                    <Route path="/services/table-chair-rentals" element={<TableChairRentals />} />
                    <Route path="/services/tssa-inflatables" element={<TSSAInflatables />} />
                    <Route path="/services/lawn-signs" element={<LawnSigns />} />
                    <Route path="/services/inflatable-sports" element={<InflatableSports />} />
                    <Route path="/services/electronic-games" element={<ElectronicGames />} />
                    <Route path="/packages" element={<Packages />} />
                    <Route path="/packages/:packageId" element={<PackageBuilder />} />
                    <Route path="/service-area" element={<ServiceArea />} />
                    <Route path="/service-area/windsor" element={<Windsor />} />
                    <Route path="/service-area/essex" element={<Essex />} />
                    <Route path="/service-area/sarnia-lambton" element={<SarniaLambton />} />
                    <Route path="/service-area/london" element={<London />} />
                    <Route path="/service-area/chatham-kent" element={<ChathamKent />} />
                    <Route path="/service-area/lasalle" element={<LaSalle />} />
                    <Route path="/service-area/tecumseh" element={<Tecumseh />} />
                    <Route path="/service-area/amherstburg" element={<Amherstburg />} />
                    <Route path="/bounce-house-rentals-windsor" element={<BounceHouseWindsor />} />
                    <Route path="/party-rentals-windsor" element={<PartyRentalsWindsor />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/events/corporate" element={<Corporate />} />
                    <Route path="/events/schools-churches" element={<Navigate to="/events/schools" replace />} />
                    <Route path="/events/schools" element={<Schools />} />
                    <Route path="/events/churches" element={<Churches />} />
                    <Route path="/events/community-municipal" element={<CommunityMunicipal />} />
                    <Route path="/events/backyard-parties" element={<BackyardParties />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/about/our-licence" element={<OurLicence />} />
                    <Route path="/reviews" element={<Reviews />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/resources" element={<Resources />} />
                    <Route path="/resources/event-planning-checklist" element={<EventPlanningChecklistArticle />} />
                    <Route path="/resources/safety-guidelines" element={<SafetyGuidelinesArticle />} />
                    <Route path="/resources/space-setup-requirements" element={<SpaceSetupArticle />} />
                    <Route path="/resources/group-size-guide" element={<GroupSizeArticle />} />
                    <Route path="/resources/venue-requirements" element={<VenueRequirementsArticle />} />
                    <Route path="/resources/what-to-expect" element={<WhatToExpectArticle />} />
                    <Route path="/resources/concession-line-flow" element={<ConcessionLineFlowArticle />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                    <Route path="/last-minute-deals" element={<LastMinuteDeals />} />
                    <Route path="/add-ons" element={<AddOns />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/rentals" element={<Rentals />} />
                    <Route path="/photo-booth" element={<PhotoBooth />} />
                    
                    {/* Individual Product Detail Pages */}
                    <Route path="/rentals/:slug" element={<RentalDetail />} />
                    
                    {/* Customer Waiver Signing Page */}
                    <Route path="/waiver/:token" element={<Waiver />} />
                    
                    {/* Customer Payment Options Page */}
                    <Route path="/pay/:quoteId" element={<PaymentOptions />} />
                    <Route path="/pay/:quoteId/success" element={<PaymentSuccess />} />
                    
                    {/* Employee Check-In Page (public) */}
                    <Route path="/checkin/:token" element={<EmployeeCheckin />} />
                    
                    {/* Legacy URL Redirects */}
                    <Route path="/home-page" element={<Navigate to="/" replace />} />
                    <Route path="/jumping-castle-rentals-windsor-page282325" element={<Navigate to="/services/bounce-houses-combos" replace />} />
                    <Route path="/jumping-castle-rentals-windsor-page" element={<Navigate to="/services/bounce-houses-combos" replace />} />
                    <Route path="/interactive-inflatable-rentals-page" element={<Navigate to="/services/interactive-inflatables" replace />} />
                    <Route path="/giant-slide-rentals-windsor-page" element={<Navigate to="/services/slides" replace />} />
                    <Route path="/toddler-inflatables-rentals-windsor-page" element={<Navigate to="/services/toddler-inflatables" replace />} />
                    <Route path="/popcorn-machine-rentals-windsor930704" element={<Navigate to="/services/party-games-concessions" replace />} />
                    <Route path="/carnival-game-rentals-windsor-page" element={<Navigate to="/services/party-games-concessions" replace />} />
                    <Route path="/birthday-lawn-signs-windsor-page" element={<Navigate to="/services/lawn-signs" replace />} />
                    <Route path="/table-and-chair-rentals-windsor-page" element={<Navigate to="/services/table-chair-rentals" replace />} />
                    <Route path="/bouncy-castle-rentals" element={<Navigate to="/services/bounce-houses-combos" replace />} />
                    <Route path="/bouncy-castle-rentals-page" element={<Navigate to="/services/bounce-houses-combos" replace />} />
                    <Route path="/birthday-lawn-signs-windsor" element={<Navigate to="/services/lawn-signs" replace />} />
                    <Route path="/miniature-golf-game-rentals-windsor" element={<Navigate to="/services/party-games-concessions" replace />} />
                    <Route path="/birthday-parties-windsor-127978" element={<Navigate to="/services" replace />} />
                    <Route path="/contact-us402826" element={<Navigate to="/contact" replace />} />
                    <Route path="/inflatable-water-slide-rentals-windsor-page" element={<Navigate to="/services/slides" replace />} />
                    <Route path="/school-event-rentals-507880" element={<Navigate to="/services" replace />} />
                    <Route path="/inflatables-obstacle-course-rentals-windsor" element={<Navigate to="/services/obstacle-courses" replace />} />
                    <Route path="/church-event-rentals-170766" element={<Navigate to="/services" replace />} />
                    <Route path="/bounce-house-rentals-windsor-374422" element={<Navigate to="/bounce-house-rentals-windsor" replace />} />
                    <Route path="/dunk-tank-rentals-windsor" element={<Navigate to="/services/dunk-tanks" replace />} />
                    <Route path="/carnival-game-rentals-windsor" element={<Navigate to="/services/party-games-concessions" replace />} />
                    <Route path="/giant-slide-rentals-windsor" element={<Navigate to="/services/slides" replace />} />
                    <Route path="/birthday-parties-windsor-702495" element={<Navigate to="/services" replace />} />
                    <Route path="/interactive-inflatable-rentals" element={<Navigate to="/services/interactive-inflatables" replace />} />
                    <Route path="/toddler-inflatables-rentals-windsor" element={<Navigate to="/services/toddler-inflatables" replace />} />
                    <Route path="/inflatable-game-rentals-windsor" element={<Navigate to="/services/interactive-inflatables" replace />} />
                    <Route path="/faqs" element={<Navigate to="/faq" replace />} />
                    <Route path="/school-events" element={<Navigate to="/services" replace />} />
                    <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                    <Route path="/jumping-castle-rentals-windsor" element={<Navigate to="/services/bounce-houses-combos" replace />} />
                    <Route path="/jumping-castle-" element={<Navigate to="/services/bounce-houses-combos" replace />} />
                    <Route path="/corporate-events877540" element={<Navigate to="/services" replace />} />
                    <Route path="/birthday-parties-windsor" element={<Navigate to="/services" replace />} />
                    <Route path="/dunk-tanks-windsor" element={<Navigate to="/services/dunk-tanks" replace />} />
                    <Route path="/picnic-games" element={<Navigate to="/services/party-games-concessions" replace />} />
                    <Route path="/about-us" element={<Navigate to="/about" replace />} />
                    <Route path="/table-and-chair-rentals-windsor" element={<Navigate to="/services/table-chair-rentals" replace />} />
                    <Route path="/delivery-areas" element={<Navigate to="/service-area" replace />} />
                    <Route path="/corporate-events-windsor" element={<Navigate to="/services" replace />} />
                    <Route path="/popcorn-machine-rentals-windsor" element={<Navigate to="/services/party-games-concessions" replace />} />
                    <Route path="/miniature-golf-rentals-windsor" element={<Navigate to="/services/party-games-concessions" replace />} />
                    <Route path="/tos" element={<Navigate to="/terms" replace />} />
                    
                    {/* Admin Routes - Protected by authentication */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                    <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                    <Route path="/admin/pipeline" element={<AdminRoute><PipelineAdmin /></AdminRoute>} />
                    <Route path="/admin/calendar" element={<AdminRoute><AdminCalendar /></AdminRoute>} />
                    <Route path="/admin/routes" element={<AdminRoute><AdminRoutes /></AdminRoute>} />
                    <Route path="/admin/inventory" element={<AdminRoute><InventoryAdmin /></AdminRoute>} />
                    <Route path="/admin/inventory/upload" element={<AdminRoute><InventoryUpload /></AdminRoute>} />
                    <Route path="/admin/contacts" element={<AdminRoute><ContactsAdmin /></AdminRoute>} />
                    <Route path="/admin/contacts/upload" element={<AdminRoute><ContactsUpload /></AdminRoute>} />
                    <Route path="/admin/bookings/upload" element={<AdminRoute><BookingsUpload /></AdminRoute>} />
                    <Route path="/admin/quotes" element={<AdminRoute><QuotesAdmin /></AdminRoute>} />
                    <Route path="/admin/users" element={<AdminRoute><UsersAdmin /></AdminRoute>} />
                    <Route path="/admin/settings" element={<AdminRoute><SettingsAdmin /></AdminRoute>} />
                    <Route path="/admin/utm-links" element={<AdminRoute><UTMLinksAdmin /></AdminRoute>} />
                    <Route path="/admin/analytics" element={<AdminRoute><AnalyticsAdmin /></AdminRoute>} />
                    <Route path="/admin/accounting" element={<AdminRoute><AccountingAdmin /></AdminRoute>} />
                    <Route path="/admin/sales" element={<AdminRoute><SalesAdmin /></AdminRoute>} />
                    <Route path="/admin/employees" element={<AdminRoute><EmployeesAdmin /></AdminRoute>} />
                    <Route path="/admin/timelogs" element={<Navigate to="/admin/time-logs" replace />} />
                    <Route path="/admin/time-logs" element={<AdminRoute><TimeLogsAdmin /></AdminRoute>} />
                    <Route path="/admin/categories" element={<AdminRoute><CategoriesAdmin /></AdminRoute>} />
                    <Route path="/admin/website-health" element={<AdminRoute><WebsiteHealth /></AdminRoute>} />
                    <Route path="/admin/website-health/tasks" element={<AdminRoute><WebsiteHealthTasks /></AdminRoute>} />
                    <Route path="/admin/website-health/runs" element={<AdminRoute><WebsiteHealthRuns /></AdminRoute>} />
                    <Route path="/admin/pages" element={<AdminRoute><PagesAdmin /></AdminRoute>} />
                    <Route path="/admin/promotions" element={<AdminRoute><PromotionsAdmin /></AdminRoute>} />
                    <Route path="/admin/email-log" element={<AdminRoute><EmailLogAdmin /></AdminRoute>} />
                    <Route path="/admin/cart-recovery" element={<AdminRoute><CartRecoveryAdmin /></AdminRoute>} />
                    <Route path="/admin/email-templates" element={<AdminRoute><EmailTemplatesAdmin /></AdminRoute>} />
                    <Route path="/admin/cron-jobs" element={<AdminRoute><CronJobsAdmin /></AdminRoute>} />
                    
                    {/* Driver Portal Routes - Protected by driver authentication */}
                    <Route path="/driver/login" element={<DriverLogin />} />
                    <Route path="/driver" element={
                      <DriverRoute>
                        <DriverLayout />
                      </DriverRoute>
                    }>
                      <Route index element={<DriverPortal />} />
                    </Route>
                    
                    {/* 404 Route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </UTMProvider>
            </BrowserRouter>
          </TooltipProvider>
        </QuoteProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
